import { inject, Injectable } from '@angular/core';
import { UploadsClient } from '@unity/clients';
import { bootstrapSurveyJS, bootstrapUnityQuestions } from '@unity/utils/bootstrapSurveyJS';
import { SurveyJSProperties } from '@unity/utils/types';
import { forkJoin, map, switchMap, take } from 'rxjs';
import { QuestionTextModel, SurveyModel, UploadFilesEvent, type ITheme, type Model } from 'survey-core';
import type { ICreatorTheme, SurveyCreatorModel } from 'survey-creator-core';
import type { SurveyPDF } from 'survey-pdf';

@Injectable({
  providedIn: 'root',
})
export class FormSchemaService {
  #surveyCoreModule?: typeof import('survey-core');
  #surveyCreatorModule?: typeof import('survey-creator-core');
  #surveyPdfModule?: typeof import('survey-pdf');
  #surveyAngularModule?: typeof import('survey-angular-ui');

  #bootstrapped = false;

  protected readonly uploadsClient = inject(UploadsClient);

  private async loadCoreModule() {
    if (!this.#surveyCoreModule) {
      this.#surveyCoreModule = await import('survey-core');
      if (!this.#bootstrapped) {
        bootstrapSurveyJS(this.#surveyCoreModule);
        this.#surveyAngularModule = await import('survey-angular-ui');
        await bootstrapUnityQuestions(this.#surveyAngularModule);
        this.#bootstrapped = true;
      }
    }
    return this.#surveyCoreModule;
  }

  private async loadCreatorModule() {
    await this.loadCoreModule();
    if (!this.#surveyCreatorModule) {
      this.#surveyCreatorModule = await import('survey-creator-core');
    }
    return this.#surveyCreatorModule;
  }

  private async loadPdfModule() {
    if (!this.#surveyPdfModule) {
      this.#surveyPdfModule = await import('survey-pdf');
    }
    return this.#surveyPdfModule;
  }

  private surveyTheme: ITheme = {
    isPanelless: true,
    cssVariables: {
      '--background-dim': 'rgba(255, 255, 255, 1)',
      '--sjs-base-unit': '0.25rem', // Most margins and padding are calculated based on this value, 0.25rem aligns closely with the spacing used in our custom forms.
      '--sjs-font-family': 'FranklinGothicBook',
      '--sjs-font-questiontitle-family': 'FranklinGothicDemi',
      '--sjs-font-questiondescription-family': 'FranklinGothicBook',
      '--sjs-font-questiondescription-size': '0.75rem',
      '--sjs-font-questiondescription-color': 'rgba(0, 0, 0, 1)',
      '--sjs-primary-backcolor': 'var(--kendo-color-primary-emphasis)',
      '--sjs-special-red-light': 'transparent', // This removes the red background that appears on form controls during error state.
      '--sjs-general-backcolor-dim-light': 'transparent',
      '--lbr-question-panel-expand-button-icon-width': '1.25rem', // Need to resize icons due to the reduction in the ---sjs-base-unit
      '--lbr-question-panel-expand-button-icon-height': '1.25rem',
    },
  };

  private surveyCreatorTheme: ICreatorTheme = {
    cssVariables: {
      '--background-dim': 'rgba(255, 255, 255, 1)',
      '--sjs-font-questiontitle-family': 'FranklinGothicDemi',
      '--ctr-font-unit': '6.8px',
      '--ctr-size-unit': '5.2px',
    },
  };

  private configureSurvey(model: Model) {
    model.showCompleteButton = false;
    model.showCompletePage = false;
    model.showTitle = false;
    model.applyTheme(this.surveyTheme);
    model.widthMode = 'responsive';
    model.showNavigationButtons = false;
    model.showProgressBar = false;
    model.checkErrorsMode = 'onValueChanged';
    model.textUpdateMode = 'onTyping';
    model.onUploadFiles.add((model, event) => this.uploadFiles(model, event));
    model.questionErrorLocation = 'bottom';
    model.questionDescriptionLocation = 'underInput';
  }

  private uploadFiles(model: SurveyModel, event: UploadFilesEvent) {
    const files = event.files;

    const uploadRequests = files.map(file => {
      const createOneRequest = {
        file_name: file.name,
        file_size: file.size ?? 0,
        file_type: file.type ?? '',
      };

      const updateOneRequest = {
        data: file,
        fileName: file.name,
      };

      return this.uploadsClient.createOne(createOneRequest).pipe(
        switchMap(createOne =>
          this.uploadsClient.updateOne(createOne.id, updateOneRequest).pipe(map(() => ({ content: createOne.id, file })))
        ),
        take(1)
      );
    });

    forkJoin(uploadRequests)
      .pipe(take(1))
      .subscribe(value => {
        event.callback(value);
      });
  }

  private appendCustomCss(model: Model) {
    model.css['text']['onError'] += ' unity-sjs-input--error';
    model.css['comment']['onError'] += ' unity-sjs-input--error';
    model.css['dropdown']['onError'] += ' unity-sjs-input--error';
    model.css['paneldynamic']['buttonAdd'] += ' k-button unity-sjs-paneldynamic-add-btn';
    model.css['paneldynamic']['buttonRemove'] += ' k-button unity-sjs-paneldynamic-remove-btn';
  }

  public async surveyModel(schema: unknown): Promise<Model> {
    const module = await this.loadCoreModule();

    const model = new module.Model(schema);
    this.configureSurvey(model);
    this.appendCustomCss(model);

    // handle using Date objects with date questions
    for (const question of model.getAllQuestions()) {
      if (!(question instanceof QuestionTextModel && question.inputType == 'date')) {
        continue;
      }
      if (question.value instanceof Date) {
        question.value = question.value.toISOString().substring(0, 10);
      }

      question.onPropertyChanged.add(function (_sender, { newValue }) {
        if (newValue instanceof Date) {
          question.value = newValue.toISOString().substring(0, 10);
        }
      });
    }

    return model;
  }

  public async surveyCreatorModel(): Promise<SurveyCreatorModel> {
    const module = await this.loadCreatorModule();

    const model = new module.SurveyCreatorModel({
      showSurveyTitle: false,
      showJSONEditorTab: true,
      showSimulatorInPreviewTab: false,
      showLogicTab: false,
      autoSaveEnabled: true,
    });
    model.onElementAllowOperations.add(function (_, options) {
      const isTemplateQuestion = options.element.getPropertyValue(SurveyJSProperties.UnityIsTemplateQuestion);
      if (isTemplateQuestion) {
        options.allowChangeInputType = false;
        options.allowChangeRequired = false;
        options.allowChangeType = false;
        options.allowCopy = false;
        options.allowDelete = false;
        options.allowEdit = false;
        options.allowExpandCollapse = false;
        options.allowShowSettings = false;
      }
    });

    model.applyCreatorTheme(this.surveyCreatorTheme);

    model.onTestSurveyCreated.add((_, options) => {
      this.configureSurvey(options.survey);
    });

    return model;
  }

  public async surveyPdfModel(schema: unknown): Promise<SurveyPDF> {
    const module = await this.loadPdfModule();
    const pdfModel = new module.SurveyPDF(schema, {
      fontSize: 12,
    });
    pdfModel.mode = 'display';
    return pdfModel;
  }
}
